/**
 * Hook for managing recent searches
 * 
 * Provides functionality for:
 * - Storing user's search history for autocomplete suggestions
 * - Persisting searches to localStorage
 * - Managing search history with automatic cleanup
 * - Preventing duplicate entries
 */

import { useRef, useCallback, useMemo } from 'react';

/**
 * Hook for managing recent searches
 * Stores user's search history for autocomplete suggestions
 */
export function useRecentSearches(maxCount: number = 10): {
  recentSearches: string[];
  addSearch: (query: string) => void;
  removeSearch: (query: string) => void;
  clearSearches: () => void;
} {
  const recentSearchesRef = useRef<string[]>([]);

  /**
   * Add a new search to recent searches
   * Prevents duplicates and maintains order (most recent first)
   */
  const addSearch = useCallback((query: string): void => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const searches = recentSearchesRef.current;
    
    // Remove existing instance if present to avoid duplicates
    const existingIndex = searches.indexOf(trimmedQuery);
    if (existingIndex > -1) {
      searches.splice(existingIndex, 1);
    }
    
    // Add to beginning (most recent)
    searches.unshift(trimmedQuery);
    
    // Limit to maxCount to prevent unlimited growth
    if (searches.length > maxCount) {
      searches.splice(maxCount);
    }
    
    // Persist to localStorage for cross-session persistence
    try {
      localStorage.setItem('dinnerBrain_recentSearches', JSON.stringify(searches));
    } catch (_error) {
      // console.warn('Failed to save recent searches to localStorage:', error);
    }
  }, [maxCount]);

  /**
   * Remove a specific search from recent searches
   */
  const removeSearch = useCallback((query: string): void => {
    const searches = recentSearchesRef.current;
    const index = searches.indexOf(query);
    
    if (index > -1) {
      searches.splice(index, 1);
      
      // Update localStorage
      try {
        localStorage.setItem('dinnerBrain_recentSearches', JSON.stringify(searches));
      } catch (_error) {
        // console.warn('Failed to save recent searches to localStorage:', error);
      }
    }
  }, []);

  /**
   * Clear all recent searches
   */
  const clearSearches = useCallback((): void => {
    recentSearchesRef.current = [];
    
    // Remove from localStorage
    try {
      localStorage.removeItem('dinnerBrain_recentSearches');
    } catch (_error) {
      // console.warn('Failed to clear recent searches from localStorage:', error);
    }
  }, []);

  /**
   * Initialize from localStorage on mount
   * This runs only once and loads persisted searches
   */
  useMemo(() => {
    try {
      const stored = localStorage.getItem('dinnerBrain_recentSearches');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          // Ensure we don't exceed maxCount when loading
          recentSearchesRef.current = parsed.slice(0, maxCount);
        }
      }
    } catch (_error) {
      // console.warn('Failed to load recent searches from localStorage:', error);
      // Initialize with empty array on error
      recentSearchesRef.current = [];
    }
  }, [maxCount]);

  return {
    recentSearches: recentSearchesRef.current,
    addSearch,
    removeSearch,
    clearSearches,
  };
}